import { dayjs } from "utils";
import { useEffect, useMemo } from "react";
import intersection from "lodash-es/intersection";
import keyBy from "lodash-es/keyBy";
import { ChartDataType } from "../components/Chat/Cards/EcosystemDetail";
import { ChatCategoryCardType } from "../components/Chat/Cards/type";
import { KlineEntry, TvlEntry } from "../features/api/ChatApi";
import { useIsPc } from "@/src/hooks/useIsPc";

// 封装的工具函数：检查token数据是否有效
const isValidTokenData = (tokenData: any): boolean => {
  if (
    tokenData &&
    (typeof tokenData.price === "number" ||
      (typeof tokenData.price === "string" && tokenData.price) ||
      (typeof tokenData.val === "string" && tokenData.val))
  ) {
    return true;
  }
  return false;
};

export const useLineChart = (
  cardItem: ChatCategoryCardType,
  selectedDataType?: ChartDataType,
  handleShowChart?: (e: any) => void,
  tokenKlineData?: Record<string, KlineEntry[] | TvlEntry[]>,
) => {
  const {
    tvl,
    token_kline,
    properties: { token_symbol, icon },
    type,
  } = cardItem;
  const [isPC] = useIsPc();
  const isEcosystem = type === "ecosystem";
  const isDefiTvl = isEcosystem && selectedDataType === ChartDataType.DEFI_TVL;
  const tokenLineData = token_kline || tokenKlineData;
  const sourceData = isDefiTvl ? tvl : tokenLineData;
  const isSearchETH = token_symbol === "ETH";
  const isSearchSOL = token_symbol === "SOL";
  const isSearchETHSOL = isSearchETH || isSearchSOL;
  const priceName = isDefiTvl ? "val" : "price";

  const { timestampList, tokenDataList } = useMemo(() => {
    const tokenList = Object.keys(sourceData || {});
    if (tokenList.length === 0) {
      return { timestampList: [], tokenDataList: {} };
    }
    let timestampList: number[] = [];
    const tokenDataList: Record<
      string,
      Record<string, { timestamp: number; price?: number; val?: number }>
    > = {};
    const timestampMap: Record<string, number[]> = {};
    tokenList.forEach((tokenKey) => {
      const tokenListData = Array.isArray(sourceData?.[tokenKey])
        ? sourceData?.[tokenKey]
        : [];
      timestampMap[tokenKey] = tokenListData.map((item) => {
        // @ts-expect-error date or timestamp
        return item.date || item.timestamp;
      });
      // @ts-expect-error date or timestamp
      tokenDataList[tokenKey] = keyBy(
        tokenListData,
        // @ts-expect-error date or timestamp
        tokenListData?.[0]?.timestamp ? "timestamp" : "date",
      );
    });
    timestampList = intersection(...Object.values(timestampMap));
    timestampList.sort((a, b) => a - b);

    return {
      timestampList,
      tokenDataList,
    };
  }, [sourceData]);

  const tvlEthData = useMemo(() => {
    if (!isSearchETHSOL || !tokenDataList.ETH) {
      return [];
    }
    return timestampList.map((timestamp) => {
      const tokenData = tokenDataList.ETH?.[timestamp];

      if (isValidTokenData(tokenData)) {
        return Number(tokenData[priceName]);
      }
      return null;
    });
  }, [isSearchETHSOL, timestampList, tokenDataList.ETH, priceName]);

  const tvlSOLData = useMemo(() => {
    if (!isSearchETHSOL || !tokenDataList.SOL) {
      return [];
    }
    return timestampList.map((timestamp) => {
      const tokenData = tokenDataList.SOL?.[timestamp];
      if (isValidTokenData(tokenData)) {
        return Number(tokenData[priceName]);
      }
      return null;
    });
  }, [isSearchETHSOL, timestampList, tokenDataList.SOL, priceName]);

  const [searchKey, searchTokenData] = useMemo(() => {
    if (isSearchETHSOL) {
      return [token_symbol, isSearchETH ? tvlEthData : tvlSOLData];
    }
    const currentKey = token_symbol;
    const tokenData = timestampList.map((timestamp) => {
      const tokenCurrData = tokenDataList?.[currentKey || ""]?.[timestamp];
      if (isValidTokenData(tokenCurrData)) {
        return Number(tokenCurrData?.[priceName]);
      }
      return null;
    });

    return [currentKey, tokenData];
  }, [
    isSearchETHSOL,
    token_symbol,
    timestampList,
    isSearchETH,
    tvlEthData,
    tvlSOLData,
    tokenDataList,
    priceName,
  ]);

  const xAxisData = useMemo(() => {
    return timestampList.map((timestamp) => ({
      x: timestamp,
      label: dayjs(timestamp).format(
        isDefiTvl ? "MMM DD YYYY, HH:mm" : "MMM DD, HH:mm",
      ),
    }));
  }, [isDefiTvl, timestampList]);

  const yAxisData = useMemo(() => {
    return {
      left: [
        {
          color: "var(--edgen-content_brand_primary)",
          shadow: true,
          name: searchKey,
          data: searchTokenData,
          logo: icon,
        },
      ],
    };
  }, [searchKey, searchTokenData, icon]);

  useEffect(() => {
    if (yAxisData?.left[0]?.data?.length > 30) {
      handleShowChart?.(0);
    } else {
      handleShowChart?.(1);
    }
  }, [yAxisData, handleShowChart]);

  const xKeyTicks = useMemo(() => {
    if (!xAxisData?.length) {
      return [];
    }
    const count = 5;
    const interval = Math.floor(xAxisData.length / count);
    const dataStep1Item = xAxisData[interval * 1];
    const dataStep2Item = xAxisData[interval * 2];
    const dataStep3Item = xAxisData[interval * 3];
    const dataStep4Item = xAxisData[interval * 4];
    return isPC
      ? [
          { x: dataStep1Item.x, label: dataStep1Item.label },
          { x: dataStep2Item.x, label: dataStep2Item.label },
          dataStep3Item,
          dataStep4Item,
        ]
      : [dataStep2Item, dataStep4Item];
  }, [xAxisData, isPC]);
  return {
    xAxisData,
    yAxisData,
    xKeyTicks,
  };
};
